import { useEffect, useMemo, useState, useCallback } from 'react';
import { useCurrentPdv } from '../contexts/CurrentPdvContext';
import { useAuth } from '../contexts/AuthContext';
import { pdvApi } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Pencil, Trash2, Thermometer, AlertTriangle, FileDown,
  FileSpreadsheet, ChevronLeft, ChevronRight, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const MONTH_LABELS = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
                      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const TIME_SLOTS = [
  { value: 'mattina', label: 'Mattina' },
  { value: 'pomeriggio', label: 'Pomeriggio' },
  { value: 'sera', label: 'Sera' },
];

function monthRange(month) {
  // month: 'YYYY-MM'
  const [y, m] = month.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  return { year: y, month: m, n_days: lastDay };
}

function shiftMonth(month, delta) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function todayStr() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

function currentMonthStr() {
  return todayStr().slice(0, 7);
}

const emptyBanco = {
  name: '', type: 'refrigerato', expected_min: '', expected_max: '',
  unit: '°C', marca: '', modello: '', matricola: '', posizione: '', note: '',
};

const emptyLog = {
  date: todayStr(), time_slot: 'mattina', time_hh: '',
  temperature: '', operator: '', note: '',
};

export default function BanchiFrigoPage() {
  const { user } = useAuth();
  const { currentPdvId } = useCurrentPdv();
  const [pdvs, setPdvs] = useState([]);
  const [pdvId, setPdvId] = useState(currentPdvId || '');
  const [tab, setTab] = useState('banchi'); // banchi | logs

  const [types, setTypes] = useState([]);
  const [banchi, setBanchi] = useState([]);
  const [logs, setLogs] = useState([]);
  const [month, setMonth] = useState(currentMonthStr());
  const [loading, setLoading] = useState(false);

  // dialogs
  const [bancoDialogOpen, setBancoDialogOpen] = useState(false);
  const [editingBanco, setEditingBanco] = useState(null);
  const [bancoForm, setBancoForm] = useState(emptyBanco);
  const [confirmDeleteBanco, setConfirmDeleteBanco] = useState(null);

  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [logForm, setLogForm] = useState(emptyLog);
  const [logTargetBanco, setLogTargetBanco] = useState(null);
  const [confirmDeleteLog, setConfirmDeleteLog] = useState(null);

  const canEdit = useMemo(() => {
    const role = user?.role;
    return [
      'super_admin', 'admin', 'amministratore', 'rspp',
      'consulente_haccp', 'collaborator', 'gerente', 'azienda',
    ].includes(role);
  }, [user]);

  // Carica i PDV (UL)
  useEffect(() => {
    pdvApi.getAll().then((r) => {
      setPdvs(r.data || []);
      if (!pdvId && (r.data || []).length === 1) setPdvId(r.data[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carica tipologie
  useEffect(() => {
    api.get('/banchi-frigo/types').then((r) => setTypes(r.data || [])).catch(() => {});
  }, []);

  // Sync pdv dal banner globale
  useEffect(() => {
    if (currentPdvId && currentPdvId !== pdvId) setPdvId(currentPdvId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPdvId]);

  const loadBanchi = useCallback(async () => {
    if (!pdvId) { setBanchi([]); return; }
    setLoading(true);
    try {
      const r = await api.get('/banchi-frigo', { params: { pdv_id: pdvId } });
      setBanchi(r.data || []);
    } catch {
      toast.error('Errore caricamento banchi frigo');
    } finally {
      setLoading(false);
    }
  }, [pdvId]);

  const loadLogs = useCallback(async () => {
    if (!pdvId) { setLogs([]); return; }
    try {
      const r = await api.get('/banchi-frigo-logs', { params: { pdv_id: pdvId, month } });
      setLogs(r.data || []);
    } catch {
      toast.error('Errore caricamento log temperature');
    }
  }, [pdvId, month]);

  useEffect(() => { loadBanchi(); }, [loadBanchi]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleSeedDefaults = async () => {
    if (!pdvId) { toast.error('Seleziona prima una UL'); return; }
    try {
      await api.post('/banchi-frigo/seed-defaults', null, { params: { pdv_id: pdvId } });
      toast.success('Banchi frigo standard caricati');
      loadBanchi();
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Banco CRUD ---------------------------
  const openCreateBanco = () => {
    setEditingBanco(null);
    setBancoForm({ ...emptyBanco });
    setBancoDialogOpen(true);
  };

  const openEditBanco = (b) => {
    setEditingBanco(b);
    setBancoForm({
      name: b.name || '',
      type: b.type || 'refrigerato',
      expected_min: b.expected_min ?? '',
      expected_max: b.expected_max ?? '',
      unit: b.unit || '°C',
      marca: b.marca || '',
      modello: b.modello || '',
      matricola: b.matricola || '',
      posizione: b.posizione || '',
      note: b.note || '',
    });
    setBancoDialogOpen(true);
  };

  const handleTypeChange = (newType) => {
    const t = types.find((x) => x.code === newType);
    setBancoForm((f) => ({
      ...f,
      type: newType,
      expected_min: t && t.min !== null ? t.min : f.expected_min,
      expected_max: t && t.max !== null ? t.max : f.expected_max,
    }));
  };

  const saveBanco = async () => {
    if (!pdvId) { toast.error('Seleziona UL'); return; }
    if (!bancoForm.name?.trim()) { toast.error('Nome obbligatorio'); return; }
    const payload = {
      ...bancoForm,
      pdv_id: pdvId,
      expected_min: bancoForm.expected_min === '' ? null : Number(bancoForm.expected_min),
      expected_max: bancoForm.expected_max === '' ? null : Number(bancoForm.expected_max),
    };
    try {
      if (editingBanco) {
        await api.put(`/banchi-frigo/${editingBanco.id}`, payload);
        toast.success('Banco frigo aggiornato');
      } else {
        await api.post('/banchi-frigo', payload);
        toast.success('Banco frigo creato');
      }
      setBancoDialogOpen(false);
      loadBanchi();
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  const deleteBanco = async () => {
    if (!confirmDeleteBanco) return;
    try {
      await api.delete(`/banchi-frigo/${confirmDeleteBanco.id}`);
      toast.success('Banco frigo eliminato (e relativi log)');
      setConfirmDeleteBanco(null);
      loadBanchi();
      loadLogs();
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Log CRUD -----------------------------
  const openCreateLog = (banco = null) => {
    setEditingLog(null);
    setLogTargetBanco(banco);
    setLogForm({ ...emptyLog, date: todayStr() });
    setLogDialogOpen(true);
  };

  const openEditLog = (lg) => {
    setEditingLog(lg);
    setLogTargetBanco(banchi.find((b) => b.id === lg.banco_frigo_id) || null);
    setLogForm({
      date: lg.date || todayStr(),
      time_slot: lg.time_slot || 'mattina',
      time_hh: lg.time_hh || '',
      temperature: lg.temperature ?? '',
      operator: lg.operator || user?.full_name || '',
      note: lg.note || '',
    });
    setLogDialogOpen(true);
  };

  const saveLog = async () => {
    if (!pdvId) { toast.error('Seleziona UL'); return; }
    if (!logTargetBanco?.id) { toast.error('Seleziona un banco frigo'); return; }
    if (logForm.temperature === '' || logForm.temperature === null) {
      toast.error('Inserisci la temperatura'); return;
    }
    const payload = {
      pdv_id: pdvId,
      banco_frigo_id: logTargetBanco.id,
      date: logForm.date,
      time_slot: logForm.time_slot,
      time_hh: logForm.time_hh || null,
      temperature: Number(logForm.temperature),
      operator: logForm.operator || user?.full_name || '',
      note: logForm.note || null,
    };
    try {
      if (editingLog) {
        await api.put(`/banchi-frigo-logs/${editingLog.id}`, payload);
        toast.success('Rilevazione aggiornata');
      } else {
        await api.post('/banchi-frigo-logs', payload);
        toast.success('Rilevazione registrata');
      }
      setLogDialogOpen(false);
      loadLogs();
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  const deleteLog = async () => {
    if (!confirmDeleteLog) return;
    try {
      await api.delete(`/banchi-frigo-logs/${confirmDeleteLog.id}`);
      toast.success('Rilevazione eliminata');
      setConfirmDeleteLog(null);
      loadLogs();
    } catch (e) {
      toast.error('Errore: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Export -------------------------------
  const downloadSheet = async (format) => {
    if (!pdvId) { toast.error('Seleziona UL'); return; }
    try {
      const res = await api.get(`/banchi-frigo-logs/sheet/${format}`, {
        params: { pdv_id: pdvId, month },
        responseType: 'blob',
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([res.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scheda_temperature_${month}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore export: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Grid mensile
  const monthInfo = useMemo(() => monthRange(month), [month]);
  const logsByBancoDay = useMemo(() => {
    const map = {};
    for (const b of banchi) {
      map[b.id] = {};
      for (let d = 1; d <= monthInfo.n_days; d++) map[b.id][d] = [];
    }
    for (const lg of logs) {
      const bid = lg.banco_frigo_id;
      if (!map[bid]) continue;
      const day = Number((lg.date || '').split('-')[2]);
      if (map[bid][day]) map[bid][day].push(lg);
    }
    return map;
  }, [logs, banchi, monthInfo]);

  const outOfRangeCount = useMemo(() => logs.filter((l) => l.out_of_range).length, [logs]);

  return (
    <div className="space-y-4" data-testid="banchi-frigo-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Thermometer className="w-6 h-6 text-primary" />
            Banchi Frigo & Schede Temperature
          </h1>
          <p className="text-sm text-muted-foreground">
            Gestione delle unità refrigeranti per UL e rilevazioni temperature giornaliere (HACCP).
          </p>
        </div>
      </div>

      {/* Selettore UL */}
      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label>Unità Locale</Label>
            <Select value={pdvId || ''} onValueChange={(v) => setPdvId(v)}>
              <SelectTrigger data-testid="banchi-frigo-pdv-select">
                <SelectValue placeholder="Seleziona una UL" />
              </SelectTrigger>
              <SelectContent>
                {pdvs.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name || p.company_name} {p.city ? `— ${p.city}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Mese di riferimento</Label>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, -1))}
                      data-testid="month-prev-btn">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="text-center"
                     data-testid="month-input" />
              <Button variant="outline" size="icon" onClick={() => setMonth(shiftMonth(month, 1))}
                      data-testid="month-next-btn">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-1 md:items-end">
            {outOfRangeCount > 0 && (
              <Badge variant="destructive" className="w-fit gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                {outOfRangeCount} rilevazioni fuori range nel mese
              </Badge>
            )}
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => { loadBanchi(); loadLogs(); }}
                      data-testid="reload-btn">
                <RefreshCw className="w-4 h-4 mr-1" /> Ricarica
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadSheet('pdf')} disabled={!pdvId}
                      data-testid="export-pdf-btn">
                <FileDown className="w-4 h-4 mr-1" /> PDF
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadSheet('excel')} disabled={!pdvId}
                      data-testid="export-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="banchi" data-testid="tab-banchi">Banchi Frigo ({banchi.length})</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Scheda Mensile ({MONTH_LABELS[monthInfo.month - 1]} {monthInfo.year})</TabsTrigger>
        </TabsList>

        {/* TAB BANCHI */}
        <TabsContent value="banchi" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-muted-foreground">
              {pdvId
                ? 'Configura le unità refrigeranti della UL selezionata (frigo, vetrina, freezer...).'
                : 'Seleziona una UL per iniziare.'}
            </div>
            {canEdit && pdvId && (
              <div className="flex gap-2">
                {banchi.length === 0 && (
                  <Button variant="outline" size="sm" onClick={handleSeedDefaults} data-testid="seed-defaults-btn">
                    Carica set standard
                  </Button>
                )}
                <Button size="sm" onClick={openCreateBanco} data-testid="add-banco-btn">
                  <Plus className="w-4 h-4 mr-1" /> Nuovo banco frigo
                </Button>
              </div>
            )}
          </div>

          {pdvId && banchi.length === 0 && !loading && (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                Nessun banco frigo configurato. {canEdit && 'Usa "Carica set standard" per partire velocemente.'}
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {banchi.map((b) => (
              <Card key={b.id} className="hover:shadow-md transition-shadow" data-testid={`banco-card-${b.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Thermometer className="w-4 h-4 text-primary" />
                        {b.name}
                      </CardTitle>
                      <Badge variant="secondary" className="mt-1 text-[10px]">
                        {types.find((t) => t.code === b.type)?.label || b.type}
                      </Badge>
                    </div>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openCreateLog(b)} title="Aggiungi rilevazione"
                                data-testid={`add-log-btn-${b.id}`}>
                          <Plus className="w-4 h-4 text-green-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditBanco(b)} title="Modifica"
                                data-testid={`edit-banco-btn-${b.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDeleteBanco(b)} title="Elimina"
                                className="text-destructive" data-testid={`delete-banco-btn-${b.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  <div className="font-medium">
                    Range: {b.expected_min ?? '—'}°C / {b.expected_max ?? '—'}°C
                  </div>
                  {b.marca && <div>Marca: {b.marca}{b.modello ? ` · ${b.modello}` : ''}</div>}
                  {b.matricola && <div>Matricola: {b.matricola}</div>}
                  {b.posizione && <div>Posizione: {b.posizione}</div>}
                  {b.note && <div className="text-muted-foreground italic">{b.note}</div>}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* TAB LOGS / SCHEDA MENSILE */}
        <TabsContent value="logs" className="space-y-3">
          {pdvId && banchi.length > 0 && canEdit && (
            <div className="flex justify-end">
              <Button size="sm" onClick={() => openCreateLog(banchi[0])} data-testid="add-log-quick-btn">
                <Plus className="w-4 h-4 mr-1" /> Nuova rilevazione
              </Button>
            </div>
          )}

          {pdvId && banchi.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                Configura prima i banchi frigo della UL.
              </CardContent>
            </Card>
          )}

          {pdvId && banchi.length > 0 && (
            <div className="border rounded-md overflow-x-auto bg-white">
              <table className="text-xs min-w-full">
                <thead>
                  <tr className="bg-blue-900 text-white">
                    <th className="px-2 py-1 text-left sticky left-0 bg-blue-900 z-10">Banco frigo</th>
                    {Array.from({ length: monthInfo.n_days }, (_, i) => i + 1).map((d) => (
                      <th key={d} className="px-1 py-1 text-center min-w-[42px]">{d}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {banchi.map((b) => (
                    <tr key={b.id} className="border-b align-top">
                      <td className="px-2 py-1 sticky left-0 bg-white z-10 font-medium">
                        <div>{b.name}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {b.expected_min ?? '—'} / {b.expected_max ?? '—'}°C
                        </div>
                      </td>
                      {Array.from({ length: monthInfo.n_days }, (_, i) => i + 1).map((d) => {
                        const dayLogs = (logsByBancoDay[b.id] || {})[d] || [];
                        const hasOut = dayLogs.some((l) => l.out_of_range);
                        return (
                          <td
                            key={d}
                            className={`px-1 py-1 text-center border-l cursor-pointer hover:bg-blue-50
                              ${hasOut ? 'bg-red-50' : ''}`}
                            onClick={() => {
                              if (!canEdit) return;
                              const yyyymm = month;
                              const dateStr = `${yyyymm}-${String(d).padStart(2, '0')}`;
                              setEditingLog(null);
                              setLogTargetBanco(b);
                              setLogForm({ ...emptyLog, date: dateStr });
                              setLogDialogOpen(true);
                            }}
                            data-testid={`log-cell-${b.id}-${d}`}
                          >
                            {dayLogs.length === 0 ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              dayLogs.map((l) => (
                                <div
                                  key={l.id}
                                  className={`leading-tight ${l.out_of_range ? 'text-red-700 font-bold' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (canEdit) openEditLog(l);
                                  }}
                                  title={`${l.time_slot || ''} ${l.time_hh || ''} - ${l.operator || ''}`}
                                >
                                  {l.temperature}
                                </div>
                              ))
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 py-2 text-xs text-muted-foreground border-t flex items-center gap-3">
                <span className="inline-block w-3 h-3 bg-red-50 border border-red-200"></span>
                Fuori range
                <span>· Clicca su una cella per aggiungere/modificare una rilevazione</span>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* DIALOG BANCO */}
      <Dialog open={bancoDialogOpen} onOpenChange={setBancoDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingBanco ? 'Modifica banco frigo' : 'Nuovo banco frigo'}</DialogTitle>
            <DialogDescription>
              Definisci il tipo e i range di temperatura accettati (HACCP).
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Nome *</Label>
              <Input value={bancoForm.name}
                     onChange={(e) => setBancoForm({ ...bancoForm, name: e.target.value })}
                     placeholder="Es. Banco frigo cucina" data-testid="banco-name-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Tipologia</Label>
              <Select value={bancoForm.type} onValueChange={handleTypeChange}>
                <SelectTrigger data-testid="banco-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Temperatura min (°C)</Label>
              <Input type="number" step="0.1" value={bancoForm.expected_min}
                     onChange={(e) => setBancoForm({ ...bancoForm, expected_min: e.target.value })}
                     data-testid="banco-min-input" />
            </div>
            <div>
              <Label>Temperatura max (°C)</Label>
              <Input type="number" step="0.1" value={bancoForm.expected_max}
                     onChange={(e) => setBancoForm({ ...bancoForm, expected_max: e.target.value })}
                     data-testid="banco-max-input" />
            </div>
            <div>
              <Label>Marca</Label>
              <Input value={bancoForm.marca} onChange={(e) => setBancoForm({ ...bancoForm, marca: e.target.value })} />
            </div>
            <div>
              <Label>Modello</Label>
              <Input value={bancoForm.modello} onChange={(e) => setBancoForm({ ...bancoForm, modello: e.target.value })} />
            </div>
            <div>
              <Label>Matricola</Label>
              <Input value={bancoForm.matricola} onChange={(e) => setBancoForm({ ...bancoForm, matricola: e.target.value })} />
            </div>
            <div>
              <Label>Posizione</Label>
              <Input value={bancoForm.posizione} onChange={(e) => setBancoForm({ ...bancoForm, posizione: e.target.value })}
                     placeholder="Cucina, Sala, Magazzino..." />
            </div>
            <div className="md:col-span-2">
              <Label>Note</Label>
              <Textarea rows={2} value={bancoForm.note}
                        onChange={(e) => setBancoForm({ ...bancoForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBancoDialogOpen(false)}>Annulla</Button>
            <Button onClick={saveBanco} data-testid="save-banco-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG LOG */}
      <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLog ? 'Modifica rilevazione' : 'Nuova rilevazione temperatura'}</DialogTitle>
            <DialogDescription>
              {logTargetBanco?.name} · Range previsto {logTargetBanco?.expected_min ?? '—'} / {logTargetBanco?.expected_max ?? '—'}°C
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Banco frigo</Label>
              <Select value={logTargetBanco?.id || ''}
                      onValueChange={(v) => setLogTargetBanco(banchi.find((b) => b.id === v))}>
                <SelectTrigger data-testid="log-banco-select"><SelectValue placeholder="Seleziona banco" /></SelectTrigger>
                <SelectContent>
                  {banchi.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name} ({b.expected_min ?? '—'}/{b.expected_max ?? '—'}°C)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data *</Label>
              <Input type="date" value={logForm.date}
                     onChange={(e) => setLogForm({ ...logForm, date: e.target.value })}
                     data-testid="log-date-input" />
            </div>
            <div>
              <Label>Fascia oraria</Label>
              <Select value={logForm.time_slot}
                      onValueChange={(v) => setLogForm({ ...logForm, time_slot: v })}>
                <SelectTrigger data-testid="log-slot-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ora (opzionale)</Label>
              <Input type="time" value={logForm.time_hh}
                     onChange={(e) => setLogForm({ ...logForm, time_hh: e.target.value })} />
            </div>
            <div>
              <Label>Temperatura (°C) *</Label>
              <Input type="number" step="0.1" value={logForm.temperature}
                     onChange={(e) => setLogForm({ ...logForm, temperature: e.target.value })}
                     data-testid="log-temp-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Operatore</Label>
              <Input value={logForm.operator}
                     onChange={(e) => setLogForm({ ...logForm, operator: e.target.value })}
                     placeholder={user?.full_name || ''} />
            </div>
            <div className="md:col-span-2">
              <Label>Note</Label>
              <Textarea rows={2} value={logForm.note}
                        onChange={(e) => setLogForm({ ...logForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter className="flex justify-between">
            {editingLog && (
              <Button variant="destructive" onClick={() => { setConfirmDeleteLog(editingLog); setLogDialogOpen(false); }}
                      data-testid="delete-log-btn">
                <Trash2 className="w-4 h-4 mr-1" /> Elimina
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setLogDialogOpen(false)}>Annulla</Button>
              <Button onClick={saveLog} data-testid="save-log-btn">Salva</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CONFIRM DELETE BANCO */}
      <AlertDialog open={!!confirmDeleteBanco} onOpenChange={(o) => !o && setConfirmDeleteBanco(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il banco frigo?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminate anche tutte le rilevazioni associate a "{confirmDeleteBanco?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={deleteBanco}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CONFIRM DELETE LOG */}
      <AlertDialog open={!!confirmDeleteLog} onOpenChange={(o) => !o && setConfirmDeleteLog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la rilevazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Operazione irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={deleteLog}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
